/**
 * Super Admin "view as" (docs/specs/staff-view-as.md): "Open as customer" on a tenant, "Open as agency" on a partner.
 * staff_admin only (the server also requires an MFA-verified admin session). An optional reason, then the API mints a
 * one-time link (60 s) that opens the customer or partner portal in a new tab, signed in as Super Admin.
 */
import { useState, type FormEvent } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import type { Translate } from '@qm/i18n';
import { useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useAction } from '../kit/resource';
import { ErrorNote, Field, Notice } from '../kit/ui';
import type { ViewAsGrant, ViewAsTarget } from '../api';
import { isSuperAdmin } from '../lib/logic';
import { useAdminApi } from './layout';

/** Specific wording for the refusals a Super Admin can act on; anything else falls back to the generic error note. */
export function viewAsErrorText(t: Translate, err: unknown): string | null {
  const e = errorInfo(err);
  if (e.reason === 'mfa_required') return t('portal.admin.viewAs.mfaRequired');
  if (e.status === 404 || e.code === 'not_found') return t('portal.admin.viewAs.notAvailable');
  return null;
}

export type OpenWindow = (url: string, target: string, features: string) => unknown;

/** Mint the link and open it in a new tab. Returns the grant (or null when the API refused). */
export async function openViewAs(start: () => Promise<ViewAsGrant | null>, open: OpenWindow = (u, tg, f) => window.open(u, tg, f)): Promise<ViewAsGrant | null> {
  const grant = await start();
  if (grant?.url) open(grant.url, '_blank', 'noopener');
  return grant;
}

export function ViewAsAction({ target, kind, level = 2 }: { target: ViewAsTarget; kind: 'customer' | 'agency'; level?: 2 | 3 }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const act = useAction((r: string) => api.startViewAs(target, r));
  if (!isSuperAdmin(roles)) return null;
  const label = t(kind === 'agency' ? 'portal.admin.viewAs.openAgency' : 'portal.admin.viewAs.openCustomer');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void openViewAs(() => act.run(reason));
  };
  const specific = act.error ? viewAsErrorText(t, act.error) : null;
  return (
    <Panel data-testid={`view-as-${kind}`}>
      <Heading level={level}>{label}</Heading>
      <Text tone="muted">{t(kind === 'agency' ? 'portal.admin.viewAs.introAgency' : 'portal.admin.viewAs.introCustomer')}</Text>
      {!open ? <Button onClick={() => setOpen(true)} data-testid={`view-as-${kind}-start`}>{label}</Button> : (
        <form onSubmit={submit} data-testid={`view-as-${kind}-form`}>
          <Field label={t('portal.admin.viewAs.reason')} hint={t('portal.admin.viewAs.reasonHint')}>
            {(id) => <input id={id} name="reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <Button type="submit" variant="primary" disabled={act.pending}>{t('portal.admin.viewAs.open')}</Button>
          <Button type="button" onClick={() => setOpen(false)} disabled={act.pending}>{t('portal.admin.viewAs.cancel')}</Button>
        </form>
      )}
      {act.result ? (
        <Notice testId="view-as-opened">
          {t('portal.admin.viewAs.opened')} <a href={act.result.url} target="_blank" rel="noopener noreferrer">{t('portal.admin.viewAs.link')}</a>
        </Notice>
      ) : null}
      {specific ? <div className="qx-alert" role="alert" data-testid="view-as-error"><Text>{specific}</Text></div> : <ErrorNote error={act.error} />}
    </Panel>
  );
}
